/**
 * Shared fetch utilities with abort support and request deduplication
 */

// Global cache for in-flight requests (deduplication)
const inFlightRequests = new Map();

// Global cache for recent responses (short TTL)
const responseCache = new Map();
const CACHE_TTL = 5000; // 5 seconds

/**
 * Fetch with automatic abort on unmount, retry logic, and request deduplication
 */
export async function fetchWithAbort(url, options = {}, signal) {
  const { retries = 2, dedupe = true, cacheTTL = CACHE_TTL } = options;
  const cacheKey = `${options.method || 'GET'}:${url}:${options.body || ''}`;

  // Check response cache first
  if (dedupe && responseCache.has(cacheKey)) {
    const cached = responseCache.get(cacheKey);
    if (Date.now() - cached.timestamp < cacheTTL) {
      return { ok: true, data: cached.data, fromCache: true };
    }
    responseCache.delete(cacheKey);
  }

  // Early return if already aborted
  if (signal?.aborted) {
    return { ok: false, data: null, aborted: true };
  }

  // Check if there's already an in-flight request for this URL
  if (dedupe && inFlightRequests.has(cacheKey)) {
    const sharedResult = await inFlightRequests.get(cacheKey);
    // Check if we got aborted while waiting
    if (signal?.aborted) {
      return { ok: false, data: null, aborted: true };
    }
    // If shared request succeeded, use its data
    if (sharedResult._success) {
      return { ok: true, data: sharedResult.data, fromCache: true };
    }
    // If shared request was aborted or errored, we'll make our own below
    inFlightRequests.delete(cacheKey);
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    let lastError = null;

    for (let i = 0; i <= retries; i++) {
      // Check if aborted before each attempt
      if (signal?.aborted) {
        return { _aborted: true };
      }

      try {
        const res = await fetch(url, {
          ...options,
          signal,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });

        if (res.ok) {
          const data = await res.json();
          // Cache the response
          if (dedupe) {
            responseCache.set(cacheKey, { data, timestamp: Date.now() });
          }
          return { _success: true, data };
        }

        // Non-OK response, store error and retry if we have attempts left
        lastError = `HTTP ${res.status}`;
        if (i < retries) {
          await new Promise(r => setTimeout(r, 300 * (i + 1)));
        }
      } catch (e) {
        // Silently handle abort errors
        if (e.name === 'AbortError') {
          return { _aborted: true };
        }

        lastError = e.message;
        // Retry on other errors if we have attempts left
        if (i < retries) {
          await new Promise(r => setTimeout(r, 300 * (i + 1)));
        }
      }
    }

    // All retries exhausted
    return { _error: lastError || 'Failed to fetch' };
  })();

  // Store in-flight request for deduplication
  if (dedupe) {
    inFlightRequests.set(cacheKey, fetchPromise);
    fetchPromise.finally(() => {
      // Remove from in-flight after a small delay to catch near-simultaneous requests
      setTimeout(() => inFlightRequests.delete(cacheKey), 100);
    });
  }

  const result = await fetchPromise;

  if (result._aborted) {
    return { ok: false, data: null, aborted: true };
  }
  if (result._error) {
    return { ok: false, data: null, error: result._error };
  }
  return { ok: true, data: result.data };
}

/**
 * Clear all caches (useful for forcing fresh data)
 */
export function clearFetchCache() {
  responseCache.clear();
  inFlightRequests.clear();
}
